/* eslint-disable react/prop-types, react/jsx-props-no-spreading */
import React from 'react';
import range from 'lodash/range';
import find from 'lodash/find';

import { Select, Typography } from '@atoms';
import { TextField, CheckboxField, SortableArrayField, ContentModuleModal, ColorField } from '@molecules';
import MediaField from '@organisms/MediaField';
import useEventColors from '@hooks/useEventColors';

import Form from '../Form';

import { FORM_FIELDS, SECTIONS_COUNT, SECTION_FIELDS, INITIAL_VALUES } from './constants';
import Item from './Item';
import LinkItem from './LinkItem';
import LinkForm from './LinkForm';
import * as s from './styles.module.less';

const sectionsList = range(SECTIONS_COUNT);

const UsefulLinks = ({
  visible,
  onSubmit,
  hide,
  previewImg,
  onDelete,
  initialValues: data = INITIAL_VALUES,
  errors,
}) => {
  const [form] = Form.useForm(FORM_FIELDS);
  const [sectionIdx, setSectionIdx] = React.useState(0);
  const addLinkForm = React.useRef();
  const initialValues = React.useMemo(() => form.deserialize(data), [data, form]);
  const eventColors = useEventColors();

  const handleSectionSwitch = React.useCallback(
    (idx) => {
      // eslint-disable-next-line no-unused-expressions
      addLinkForm.current?.resetFields();
      setSectionIdx(idx);
    },
    [setSectionIdx, addLinkForm],
  );

  const handleOk = React.useCallback(
    (values) => {
      onSubmit(values);
      form.resetFields();
      hide();
    },
    [form, hide, onSubmit],
  );

  const handleFailed = React.useCallback(
    ({ errorFields }) => {
      const idx = find(errorFields, (field) => field.name[0] === 'sections')?.name[1];
      if ((idx && sectionIdx !== idx) || (!idx && sectionIdx !== 0)) {
        // lets switch to first section if no errors in sections found
        setSectionIdx(idx || 0);
      }
    },
    [sectionIdx],
  );

  const handleCancel = () => {
    form.resetFields();
    hide();
  };

  const handleAddLink = React.useCallback(
    (value) => {
      const links = form.getFieldValue([FORM_FIELDS.SECTIONS.name, sectionIdx, SECTION_FIELDS.LINKS.name]) || [];
      form.setFields([
        {
          name: [FORM_FIELDS.SECTIONS.name, sectionIdx, SECTION_FIELDS.LINKS.name],
          value: [value, ...links],
        },
      ]);
    },
    [form, sectionIdx],
  );

  return (
    <ContentModuleModal
      visible={visible}
      title="Useful links"
      subtitle={
        <>
          A selection of links with a featured image and title.
          <br />
          {/* lets assume sections count never be less than 2 */}
          There are {SECTIONS_COUNT} sections available to use.
        </>
      }
      okText={onDelete ? 'SAVE CHANGES' : 'ADD USEFUL LINKS'}
      onCancel={handleCancel}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Form
        form={form}
        layout="vertical"
        wrapperCol={{ span: 24 }}
        initialValues={initialValues}
        onSerializedFinish={handleOk}
        onFinishFailed={handleFailed}
      >
        <TextField {...FORM_FIELDS.TITLE} />
        <TextField {...FORM_FIELDS.DESCRIPTION} />
        <ColorField {...FORM_FIELDS.BACKGROUND_COLOR} mergeColors={eventColors} />
        <MediaField {...FORM_FIELDS.IMAGE} />
        <Typography.Title level={3}>Choose a section to edit</Typography.Title>
        <Form.Item className={s.sectionsSelector}>
          <Select onChange={handleSectionSwitch} value={sectionIdx}>
            {sectionsList.map((idx) => (
              <Select.Option key={idx} value={idx}>
                {`Section ${idx + 1}`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.List {...FORM_FIELDS.SECTIONS}>
          {(sections) =>
            sections.map(({ name, key }, idx) => (
              <div style={{ display: idx !== sectionIdx ? 'none' : null }} key={key}>
                <CheckboxField {...SECTION_FIELDS.SHOW} name={[name, SECTION_FIELDS.SHOW.name]} className={s.section} />
                <TextField {...SECTION_FIELDS.TITLE} name={[name, SECTION_FIELDS.TITLE.name]} />
              </div>
            ))
          }
        </Form.List>
      </Form>

      <LinkForm ref={addLinkForm} onSubmit={handleAddLink} />

      <Form
        form={form}
        layout="vertical"
        wrapperCol={{ span: 24 }}
        className={s.links}
        initialValues={initialValues}
        onSerializedFinish={handleOk}
        onFinishFailed={handleFailed}
      >
        <Form.List {...FORM_FIELDS.SECTIONS}>
          {(sections) =>
            sections.map(({ name, key }, idx) => (
              <SortableArrayField
                {...SECTION_FIELDS.LINKS}
                key={key}
                name={[name, SECTION_FIELDS.LINKS.name]}
                withPlaceholder
                titleLevel={4}
                style={{ display: idx !== sectionIdx ? 'none' : null }}
              >
                {(link) => <LinkItem link={link} key={link} />}
              </SortableArrayField>
            ))
          }
        </Form.List>
      </Form>
    </ContentModuleModal>
  );
};

UsefulLinks.Item = Item;

export default UsefulLinks;
